import React, { useState } from 'react';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import { DataProviderInstance } from '../../../../api/DataProvider';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import usePlanLock from '../../../../hooks/usePlanLock';
import { SUBSCRIPTION_PERMISSIONS } from '../../../../consts/SubscriptionFeatures';
import useReservationRelease from '../../../appointments/v2/components/ReserveSlotDrawer/useReservationRelease';
import ReleaseReservationDialog from '../../../appointments/v2/components/ReserveSlotDrawer/ReleaseReservationDialog';
import ReservationShareDialog from '../../../appointments/v2/components/ReserveSlotDrawer/ReservationShareDialog';
import PatientItem from '../shared/PatientItem';
import IconAction from '../shared/IconAction';
import { formatDayLabel, formatTime, formatTimeAgo, toLocalDate, toYMD } from '../../utils/dashboardDates';
import styles from './ActionCenter.module.css';

const NOT_FOUND = 'Reserva não encontrada.';

/**
 * Horário reservado pelo link de cadastro que ainda não foi preenchido
 * (get_dashboard.php, PendingReservations). As ações são as do painel da
 * reserva na agenda (ReservationPanel): lembrar do cadastro e liberar o horário,
 * com o mesmo aviso pelo WhatsApp.
 */
function ReservationItem({ item, onResolved }) {
  const { GetAppointmentReservation, ResendAppointmentReservation, GetResourceById, GetProcedureById } = DataProviderInstance();
  const planLock = usePlanLock(SUBSCRIPTION_PERMISSIONS.PATIENT_REGISTRATION_LINK, 'A reserva de horário com link de cadastro');

  // Dados do painel (reservations/get.php), carregados só ao liberar: trazem o número próprio e o horário
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const [shareData, setShareData] = useState(null);
  const release = useReservationRelease({ appointmentId: item.appointmentId, data, onReleased: () => onResolved(item.id) });

  const professional = item.resource ? GetResourceById(item.resource)?.Label || '' : '';
  const procedure = item.procedureName || (item.procedure ? GetProcedureById(item.procedure)?.Label || '' : '');
  const when = `${formatDayLabel(item.date)} às ${formatTime(item.date)}`;
  const linkExpired = item.linkExpired && !resent;
  const isBusy = busy || release.isBusy;

  function handleViewAgenda() {
    window.open(`/schedule/appointments?date=${toYMD(toLocalDate(item.date))}`, '_blank');
  }

  function handleResend() {
    setBusy(true);
    ResendAppointmentReservation(item.appointmentId, (res) => {
      setBusy(false);
      if (res?.success) {
        setResent(true);
        setShareData(res);
        return;
      }
      // Já se cadastrou ou o horário foi liberado em outra tela: sai da lista
      if (res?.error === NOT_FOUND) {
        onResolved(item.id);
        return;
      }
      alert(res?.error || res?.message || 'Não foi possível enviar o lembrete. Tente novamente.');
    });
  }

  function handleRelease() {
    setBusy(true);
    GetAppointmentReservation(item.appointmentId, (res) => {
      setBusy(false);
      if (!res?.success) {
        onResolved(item.id);
        return;
      }
      setData(res);
      release.openConfirm();
    });
  }

  function finishAfterNotice(openWhatsApp) {
    if (openWhatsApp) release.openReleasedInWhatsApp();
    onResolved(item.id);
  }

  return (
    <>
      <PatientItem
        name={item.phone}
        tone="amber"
        sent={resent}
        avatar={<HourglassEmptyOutlinedIcon style={{ fontSize: 18 }} />}
        nameTitle="Ver na agenda"
        onNameClick={handleViewAgenda}
        meta={(
          <>
            <span className={styles.tagReserve}>Reservado {formatTimeAgo(item.createdAt)}</span>
            {linkExpired && <>{' · '}<span className={styles.tagAlert}>Link vencido</span></>}
            {item.slotPast && <>{' · '}<span className={styles.tagAlert}>Horário já passou</span></>}
            {' · '}{[when, professional, procedure].filter(Boolean).join(' · ')}
          </>
        )}
        actions={(
          <>
            <IconAction
              title={item.slotPast ? 'O horário já passou: libere e reserve outro' : 'Lembrar do cadastro'}
              onClick={planLock.guard(handleResend)}
              disabled={isBusy || item.slotPast}
            >
              <SendOutlinedIcon style={{ fontSize: 16 }} />
            </IconAction>
            <IconAction title="Ver na agenda" onClick={handleViewAgenda}>
              <CalendarTodayOutlinedIcon style={{ fontSize: 15 }} />
            </IconAction>
            <IconAction title="Liberar horário" onClick={handleRelease} tone="danger" disabled={isBusy}>
              <EventBusyOutlinedIcon style={{ fontSize: 17 }} />
            </IconAction>
          </>
        )}
      />

      <ReleaseReservationDialog release={release} />

      <ReservationShareDialog isOpen={!!shareData} data={shareData} onClose={() => setShareData(null)} />

      {/* Horário já liberado, mas o aviso pelo número próprio falhou */}
      <ConfirmDialog
        isOpen={release.noticeFailed}
        title="Horário liberado"
        message="O horário foi liberado, mas o aviso pelo WhatsApp não saiu. Envie o aviso pela conversa."
        confirmLabel="Abrir no WhatsApp"
        cancelLabel="Fechar"
        variant="warning"
        onConfirm={() => finishAfterNotice(true)}
        onCancel={() => finishAfterNotice(false)}
      />

      {planLock.modal}
    </>
  );
}

export default ReservationItem;
